<template>
  <div class="reserve-transactions-container">
    <div class="filters-and-search-container">
      <TableFilterOption
        :disabled="loading.all"
        table-label="ReserveTransactions"
        :date-filter-duration="12"
        v-bind="tableFilterOptionConfig"
        :enable-filters="['dateFilter', 'statusFilter', 'typeFilter', 'categoryFilter', 'currencyFilter']"
        ref="tableFilterOptionComponent"
        @childApplyFilter="applyFilter"
        @childClearFilter="applyFilter"
        class="filter-container"
      />
    </div>

    <Search
      :placeholder="$t('transactions.search')"
      @search="searchTransactionById"
      :disabled="transactions === null || transactions.length === 0"
      class="search-container"
    />

    <div class="header-container">
      <div class="transaction-header-count">
        <div v-if="shouldShowCount" class="showing-count-top" :class="{'hide-loader': loading.all || transactions.length === 0}">
          {{ $t('transactions.showing') }} <b class="pagination">{{ transactionsCount }}</b>
        </div>
        <div v-else class="showing-count-top" :class="{'hide-loader': loading.all}">
          {{ $t('transactions.noTransactionsFound') }}
        </div>
        <div class="loader" :class="{'hide-loader': !loading.all}"></div>
      </div>
      <export-button
        :disabled="loading.all || transactions.length === 0 || searchQuery !== ''"
        :loading="loading.export"
        :create-export="createExport"
        :tooltip-enabled="!(searchQuery === '' && transactions.length > 0)"
        :tooltip-content="tooltipContent()"
      />
    </div>

    <TableView
      :data="displayTransactions"
      :loading="loading.all"
      :infinite-scroll="searchQuery === ''"
      :all-items-loaded="allItemsLoaded"
      :columns="getColumns"
      :column-right-align="['amount', 'fees', 'net_amount', 'reserve_balance']"
      entity="reserveTransactions"
      :fixed-first-column="false"
      :clickable-rows="true"
      @row-clicked="rowClicked"
      @clearFilter="clearFilterFromEmptyPlaceholder"
    >
      <template #transaction="prop">
        <div class="transaction-column">
          <div class="status-tag-container">
            <div
              class="status"
              :class="{
                'success-status': ['SUCCESS', 'REFUNDED'].includes(prop.item.state),
                'returned-status': prop.item.state === 'RETURNED',
                'failure-status': ['FAILED', 'NOT_REFUNDED', 'INITIATION_AT_PAYFAC_FAILED'].includes(prop.item.state),
                'canceled-status': prop.item.state === 'CANCELED',
                'progress-status':
                  !['SUCCESS', 'FAILED', 'CANCELED', 'NOT_REFUNDED', 'REFUNDED', 'RETURNED', 'INITIATION_AT_PAYFAC_FAILED'].includes(prop.item.state)
              }"
            >
              {{ $t('transactions.status.' + prop.item.state) }}
            </div>
          </div>
          <div class="date-and-txid">
            <div class="date_box">
              {{ formatDateWithTimezone(prop.item.created_at.toString()) }}
            </div>
            <!-- Synthetic entries carry _transactionId (the original ID) because
                 item.id has a _source/_target suffix to keep Vue row keys unique.
                 Regular rows have no _transactionId, so item.id is used directly. -->
            <div class="txid">
              {{ prop.item._transactionId || prop.item.id }}
            </div>
          </div>
        </div>
      </template>
      
      <template #reserveType="prop">
        <div class="reserve-type-container">
          <div class="reserve-type-line multiple-reserve-type-line" v-if="isMultipleReserveTransaction(prop.item)">
            {{ $t('balanceReserve.reserveTransactions.multiple') }}
            <ToolTip
              placement="top"
              :content="getMultipleReservesTooltip(prop.item)"
            >
              <c-icon class="multiple-reserve-info-icon" name="information" :size="14" />
            </ToolTip>
          </div>
          <div class="reserve-type-line" v-else-if="getReserveTypeDisplay(prop.item)">
            {{ getReserveTypeDisplay(prop.item) }}
          </div>
          <div class="reserve-type-line" v-else>
            —
          </div>
          <div class="entry-type-badge">
            {{ getEntryLegSign(prop.item) === -1 ? $t('balanceReserve.reserveTransactions.entryType.debit') : $t('balanceReserve.reserveTransactions.entryType.credit') }}
          </div>
        </div>
      </template>
      
      <template #category="prop">
        <div class="reserve-type-container">
          <div class="reserve-type-line" v-if="getCategoryDisplay(prop.item)">
            {{ getCategoryDisplay(prop.item) }}
          </div>
          <div class="reserve-type-line" v-else>
            —
          </div>
        </div>
      </template>
      
      <template #reference_transaction_id="prop">
        <div class="reserve-type-container">
          <div class="reserve-type-line" v-if="prop.item.source_transaction_id">
            {{ prop.item.source_transaction_id }}
          </div>
          <div class="reserve-type-line" v-else>
            —
          </div>
        </div>
      </template>
      
      <template #amount="prop">
        <div class="amount-container fs-mask">
          <div class="amount">
            {{ formatter({type:'currency', data: getSignedAmount(prop.item, 'amount'), currencyCode: prop.item.currency}) }}
          </div>
          <div class="currency_code">
            {{ prop.item.currency }}
          </div>
        </div>
      </template>
      
      <template #fees="prop">
        <div class="amount-container fs-mask">
          <div class="amount" v-if="prop.item.fee">
            {{ formatter({type:'currency', data: prop.item.fee, currencyCode: prop.item.currency}) }}
          </div>
          <div class="currency_code" v-if="prop.item.fee">
            {{ prop.item.currency }}
          </div>
          <div class="empty-placeholder" v-else>
            —
          </div>
        </div>
      </template>
      
      <template #net_amount="prop">
        <div class="amount-container fs-mask">
          <div class="amount" v-if="prop.item.net_amount">
            {{ formatter({type:'currency', data: getSignedAmount(prop.item, 'net_amount'), currencyCode: prop.item.currency}) }}
          </div>
          <div class="currency_code" v-if="prop.item.net_amount">
            {{ prop.item.currency }}
          </div>
          <div class="empty-placeholder" v-else>
            —
          </div>
        </div>
      </template>
      
      <template #reserve_balance="prop">
        <div class="reserve-balance-container">
          <template v-if="getReserveSplits(prop.item).length > 0">
            <div 
              v-for="(split, index) in getReserveSplits(prop.item)" 
              :key="index"
              class="reserve-split-item"
            >
              <div class="reserve-split-line">
                <span class="reserve-label">{{ getReserveTypeLabel(split.balance_account_type) }}:</span>
                <span class="reserve-amount">{{ formatter({type:'currency', data: split.amount, currencyCode: prop.item.currency}) }}</span>
              </div>
            </div>
          </template>
          <div v-else class="empty-placeholder">
            —
          </div>
        </div>
      </template>
    </TableView>

    <ModalPopup :show="showSubmitPopup">
      <div slot="body">
        <ExportsPopUp
          :on-cancel="() => {showSubmitPopup = false}"
        />
      </div>
    </ModalPopup>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import merchantBalanceAccount from '@/api/merchantBalanceAccount';
import TableView from '@/components/common/TableView.vue';
import TableFilterOption from '@/components/common/TableFilterOption.vue';
import Search from '@/components/common/Search.vue';
import Formatter from '@/app/utils/common/functions/formatter';
import { deleteEmptyAttributes } from '@/app/utils/common/functions/functions';
import { 
  SortBy, 
  Sort,
  Currency,
  ResourceType
} from '@/api/paymentApi';
import { isNegativeAmountType, getTransactionTypeDisplayName } from '@/app/utils/common/functions/transaction-util';
import { DateFilters } from '@/app/utils/common/constants';
import { getDatePeriodFromFilter } from '@/app/utils/common/functions/dateUtils';
import {portalCheckMixin} from "@/mixin/portalPageCheck";
import {exportMixin} from "@/mixin/exportMixin";
import ModalPopup from "@/components/common/ModalPopup";
import ExportsPopUp from "@/components/exports/ExportsPopUp";
import ExportButton from "@/components/common/ExportButton.vue";
import ToolTip from "@/components/common/ToolTip.vue";
import {navigateWithParam} from "@/router";
import {RouteConstants} from "@/router/routeConstants";

export default {
  name: 'ReserveTransactionsTable',
  components: {
    TableView,
    TableFilterOption,
    Search,
    ModalPopup,
    ExportsPopUp,
    ExportButton,
    ToolTip,
  },
  mixins: [portalCheckMixin, exportMixin],
  data() {
    return {
      transactions: [],
      offsetId: null,
      listElm: null,
      pageSize: 15,
      dateFilter: {
        startDate: undefined,
        endDate: undefined,
      },
      selectedStatus: [],
      selectedReserveType: [],
      selectedCategory: [],
      selectedCurrency: [],
      allItemsLoaded: false,
      loading: {
        all: false,
        export: false
      },
      count: 0,
      searchQuery: "",
      searchResults: [],
      tableFilterOptionConfig: {},
      lastSearchedValue: null,
      showSubmitPopup: false,
      statusFilter: [
        {
          name: "SUCCESS",
        },
        {
          name: "FAILED",
        },
        {
          name: "PENDING",
        },
      ],
      reserveTypeFilterBase: [
        {
          name: "CURRENT",
        },
        {
          name: "ROLLING_RESERVE",
        },
        {
          name: "REFUND_RESERVE",
        },
        {
          name: "CHARGEBACK_RESERVE",
        },
        {
          name: "UPFRONT_RESERVE",
        },
      ],
      categoryFilterOptions: [
        { name: "PAYMENT" },
        { name: "REFUND" },
        { name: "CHARGEBACK" },
        { name: "CHARGEBACK_CREDIT" },
        { name: "PAYMENT_RETURN" },
        { name: "CHARGEBACK_FEES" },
        { name: "PAYMENT_RETURN_FEES" },
        { name: "ADJUSTMENT_REFUND_RESERVE" },
        { name: "ADJUSTMENT_CHARGEBACK_RESERVE" },
        { name: "TOP_UP" },
      ],
    }
  },
  computed: {
    ...mapGetters('app', ['getMerchantId', 'getRollingReserveConfig', 'getUpfrontReserveConfig']),
    reserveTypeFilter() {
      let filters = [...this.reserveTypeFilterBase];
      // Remove ROLLING_RESERVE if getRollingReserveConfig is null
      if (!this.getRollingReserveConfig) {
        filters = filters.filter(filter => filter.name !== 'ROLLING_RESERVE');
      }
      // Remove UPFRONT_RESERVE if getUpfrontReserveConfig is null
      if (!this.getUpfrontReserveConfig) {
        filters = filters.filter(filter => filter.name !== 'UPFRONT_RESERVE');
      }
      return this.sortFilterOptionsByDisplayLabel(filters, 'filters.typeFilter.ReserveTransactions');
    },
    sortedCategoryFilterOptions() {
      return this.sortFilterOptionsByDisplayLabel(
        this.categoryFilterOptions,
        'filters.categoryFilter.ReserveTransactions'
      );
    },
    getColumns() {
      let columns = {
        'transaction': 'balanceReserve.reserveTransactions.columns.transaction',
        'reserveType': 'balanceReserve.reserveTransactions.columns.reserveType',
        'category': 'balanceReserve.reserveTransactions.columns.category',
        'reference_transaction_id': 'balanceReserve.reserveTransactions.columns.reference_transaction_id',
        'amount': 'balanceReserve.reserveTransactions.columns.amount',
        'fees': 'balanceReserve.reserveTransactions.columns.fees',
        'net_amount': 'balanceReserve.reserveTransactions.columns.net_amount',
        'reserve_balance': 'balanceReserve.reserveTransactions.columns.reserve_balance'
      };
      return deleteEmptyAttributes(columns);
    },
    transactionsCount() {
      if (this.searchQuery !== "") {
        return this.searchResults.length + " / " + this.searchResults.length;
      } else {
        return this.transactions.length + " / " + this.count;
      }
    },
    displayTransactions() {
      const base = this.searchQuery === "" ? this.transactions : this.searchResults;
      const expanded = this.expandTransactionToEntries(base);
      if (!this.selectedReserveType || this.selectedReserveType.length === 0) {
        return expanded;
      }
      return expanded.filter(entry => {
        if (!entry._isSyntheticEntry) return true;
        return this.selectedReserveType.includes(entry._legAccountType);
      });
    },
    getErrToastOptions() {
      return { dismissable: true, timeout: 10000 };
    },
    shouldShowCount() {
      return (this.searchQuery === '' && this.count !== 0) || (this.searchQuery !== '' && this.searchResults.length > 0);
    }
  },
  mounted() {
    this.portalChecks().then(() => {
      this.initialize();
    });
  },
  methods: {
    formatter: Formatter.format,
    isNegativeAmountType,
    getTransactionTypeDisplayName,

    sortFilterOptionsByDisplayLabel(options, translationKeyPrefix) {
      return [...options].sort((a, b) => {
        const labelA = this.$t(`${translationKeyPrefix}.${a.name}`);
        const labelB = this.$t(`${translationKeyPrefix}.${b.name}`);
        return labelA.localeCompare(labelB);
      });
    },
    
    formatDateWithTimezone(date) {
      return Formatter.format({type: 'zonedDateWithTime', data: date});
    },
    
    async initialize() {
      ({ startDate: this.dateFilter.startDate, endDate: this.dateFilter.endDate } =
        getDatePeriodFromFilter(DateFilters.MONTH_TO_DATE))

      // Set the date filter storage AFTER timezone is available (portalChecks has completed),
      // then trigger TableFilterOption to read it. This avoids a race condition where
      // TableFilterOption would previously compute the date range using browser-local time
      // instead of the merchant's timezone (most visible in admin mode where timezone loads async).
      window.cbStorage.setItem('dateFilter', DateFilters.MONTH_TO_DATE);
      this.$refs.tableFilterOptionComponent?.getDateFilterFromStorage?.();

      this.listElm = document.querySelector('#infinite-list');
      if (this.listElm) {
        this.listElm.addEventListener('scroll', () => {
          if ((this.listElm.scrollTop + this.listElm.clientHeight + 0.5 >= this.listElm.scrollHeight) && this.listElm.scrollTop !== 0 && !this.loading.all) {
            this.loadMore();
          }
        });
      }

      this.tableFilterOptionConfig = {
        statusFilterOption: this.statusFilter,
        typeFilterOption: this.reserveTypeFilter,
        categoryFilterOption: this.sortedCategoryFilterOptions,
        currencyFilterOption: this.getCurrencyOptions(),
      }
      await this.loadMore();
    },
    
    async loadMore(forceLoad = false) {
      if ((this.allItemsLoaded === true || this.searchQuery !== '') && !forceLoad) return;
      this.loading.all = true;

      try {
        const params = this.getCommonQueryParams();
        const response = await merchantBalanceAccount.listReserveTransactions(
          this.getMerchantId,
          params.cursor,
          params.size,
          params.sort_by,
          params.sort,
          params.start_date,
          params.end_date,
          params.category,
          params.status,
          params.currency,
          params.reserve_type
        );
        this.setOffsetAndCount(response);
        this.transactions = this.transactions.concat(response.transactions ? response.transactions : []);

        if ((response.transactions ? response.transactions.length : 0) < this.pageSize) {
          this.allItemsLoaded = true;
        }
        this.loading.all = false;
      } catch (err) {
        this.loading.all = false;
        this.$danger(this.$t('transactions.error.message', { error: err.source?.detail }), this.getErrToastOptions);
      }
    },

    setOffsetAndCount(obj) {
      let { cursor, count } = obj;
      this.offsetId = cursor;
      this.count = count;
    },

    getCommonQueryParams() {
      return {
        cursor: this.offsetId,
        size: this.pageSize,
        sort_by: SortBy.ID,
        sort: Sort.DESC,
        start_date: this.dateFilter.startDate,
        end_date: this.dateFilter.endDate,
        status: this.selectedStatus,
        category: this.selectedCategory,
        currency: this.selectedCurrency,
        reserve_type: this.selectedReserveType,
      };
    },

    applyFilter(filter) {
      this.transactions = [];
      this.searchResults = [];
      this.offsetId = null;
      this.allItemsLoaded = false;
      this.count = 0;
      this.dateFilter.startDate = filter.dateFilterValue.startDate;
      this.dateFilter.endDate = filter.dateFilterValue.endDate;
      this.selectedStatus = filter.statusFilter;
      this.selectedReserveType = filter.typeFilter;
      this.selectedCategory = filter.categoryFilter;
      this.selectedCurrency = filter.currencyFilter;
      this.loadMore(true);
      if (this.listElm) {
        this.listElm.scrollTo(0, 0);
      }
    },

    searchTransactionById(value) {
      const trimmedValue = value ? value.trim() : "";
      if (trimmedValue === this.lastSearchedValue) return;
      this.lastSearchedValue = trimmedValue;

      if (!trimmedValue) {
        this.searchQuery = "";
        this.searchResults = [];
        this.lastSearchedValue = "";
        return;
      }
      
      this.searchQuery = trimmedValue;
      this.searchResults = this.transactions.filter(x => 
        x.id.toLowerCase().includes(trimmedValue.toLowerCase())
      );
    },

    clearFilterFromEmptyPlaceholder() {
      this.$refs.tableFilterOptionComponent.clearFilter();
    },

    rowClicked(id, data) {
      const transactionId = data._transactionId || id;
      navigateWithParam(RouteConstants.TRANSACTION_DETAILS, {transactionId, data}, {
        ...this.$route.query,
        consumerId: data?.consumer?.id,
        from: 'balance_reserve'
      })
    },

    expandTransactionToEntries(transactions) {
      return transactions.flatMap((tx) => {
        const isDebit = tx.type === 'RESERVE_ADJUSTMENT_DEBIT';
        const isCredit = tx.type === 'RESERVE_ADJUSTMENT_CREDIT';
        if ((isDebit || isCredit) && tx.reserve_type) {
          // For DEBIT: money leaves CURRENT (-) and arrives at reserve_type (+)
          // For CREDIT: money leaves reserve_type (-) and arrives at CURRENT (+)
          const sourceAccountType = isDebit ? 'CURRENT' : tx.reserve_type;
          const targetAccountType = isDebit ? tx.reserve_type : 'CURRENT';
          return [
            { ...tx, id: tx.id + '_source', _transactionId: tx.id, _isSyntheticEntry: true, _legSign: -1, _legAccountType: sourceAccountType },
            { ...tx, id: tx.id + '_target', _transactionId: tx.id, _isSyntheticEntry: true, _legSign: 1, _legAccountType: targetAccountType },
          ];
        }
        // RESERVE_TRANSFER moves funds between two reserve accounts (no CURRENT leg).
        // The backend synthesises reserve_split with [source, target] entries so the UI
        // can render both legs without any API contract change (reserve_type is the source,
        // reserve_split[0] = source, reserve_split[1] = target).
        // The backend also sets the DTO category to the target reserve's adjustment category,
        if (tx.type === 'RESERVE_TRANSFER') {
          const splits = tx.additional_info?.reserve_split;
          if (splits && splits.length >= 2) {
            return [
              { ...tx, id: tx.id + '_source', _transactionId: tx.id, _isSyntheticEntry: true, _legSign: -1, _legAccountType: splits[0].balance_account_type },
              { ...tx, id: tx.id + '_target', _transactionId: tx.id, _isSyntheticEntry: true, _legSign: 1, _legAccountType: splits[1].balance_account_type },
            ];
          }
        }
        return [tx];
      });
    },

    getEntryLegSign(transaction) {
      if (transaction._isSyntheticEntry) {
        return transaction._legSign;
      }
      return isNegativeAmountType(transaction.type) ? -1 : 1;
    },

    getSignedAmount(transaction, field) {
      const value = transaction[field];
      if (value == null) return null;
      let sign;
      if (transaction._isSyntheticEntry) {
        sign = transaction._legSign;
      } else {
        sign = isNegativeAmountType(transaction.type) ? -1 : 1;
      }
      return value * sign;
    },

    getReserveAccountType(transaction) {
      if (transaction.reserve_type) {
        return transaction.reserve_type;
      }
      return null;
    },

    getReserveTransactionAmount(transaction) {
      if (transaction.additional_info &&
        transaction.additional_info.reserve_split &&
        transaction.additional_info.reserve_split.length > 0) {
        return transaction.additional_info.reserve_split.find(reserve =>
          reserve.balance_account_type === this.getReserveAccountType(transaction)
        )?.amount || null;
      }
      return null;
    },

    getReserveSplits(transaction) {
      if (!transaction.additional_info ||
          !transaction.additional_info.reserve_split ||
          transaction.additional_info.reserve_split.length === 0) {
        return [];
      }

      // For RESERVE_TRANSFER the reserve_split is synthesised by the backend solely to
      // carry both leg account types for the two-row UI rendering. It must not be shown
      // in the Reserve Balance column, which displays fund distribution for TOP_UP only.
      if (transaction.type === 'RESERVE_TRANSFER') {
        return [];
      }

      return transaction.additional_info.reserve_split.filter(split => {
        const accountType = split.balance_account_type;
        
        // Always show CURRENT, REFUND_RESERVE, CHARGEBACK_RESERVE
        if (['CURRENT', 'REFUND_RESERVE', 'CHARGEBACK_RESERVE'].includes(accountType)) {
          return true;
        }
        
        // Only show ROLLING_RESERVE if config is present
        if (accountType === 'ROLLING_RESERVE') {
          return !!this.getRollingReserveConfig;
        }
        
        // Only show UPFRONT_RESERVE if config is present
        if (accountType === 'UPFRONT_RESERVE') {
          return !!this.getUpfrontReserveConfig;
        }
        
        return false;
      });
    },

    getReserveTypeLabel(accountType) {
      const labelMap = {
        'REFUND_RESERVE': 'Refund',
        'CHARGEBACK_RESERVE': 'Chargeback',
        'UPFRONT_RESERVE': 'Upfront',
        'ROLLING_RESERVE': 'Rolling',
        'CURRENT': 'Current Account'
      };
      return labelMap[accountType] || accountType;
    },

    getCategoryDisplayName(accountType) {
      if (!accountType) {
        return '—';
      }
      const translationKey = this.$t(`balanceReserve.reserveTypeDisplay.${accountType}`);
      return translationKey || '—';
    },

    getCategoryDisplayNameFromTransaction(category) {
      if (!category) {
        return '';
      }
      const translationKey = this.$t(`balanceReserve.transactionCategory.${category}`);
      return translationKey || '—';
    },

    getReserveTypeDisplay(transaction) {
      const accountType = transaction._legAccountType === undefined
        ? this.getReserveAccountType(transaction)
        : transaction._legAccountType;
      const reserveType = this.getCategoryDisplayName(accountType);
      
      if (!reserveType || reserveType === '—') {
        return null;
      }
      
      return reserveType;
    },

    isMultipleReserveTransaction(transaction) {
      return transaction.type === 'RESERVE' && this.getReserveSplits(transaction).length > 1;
    },

    getMultipleReservesTooltip(transaction) {
      return this.getReserveSplits(transaction)
        .map(split => this.getCategoryDisplayName(split.balance_account_type))
        .filter(label => label && label !== '—')
        .join(', ');
    },

    getCategoryDisplay(transaction) {
      const category = this.getCategoryDisplayNameFromTransaction(transaction.category);
      return category || null;
    },

    hasReserveTypeOrCategory(transaction) {
      return this.getReserveTypeDisplay(transaction) || this.getCategoryDisplay(transaction);
    },

    getReserveTypeWithCategory(transaction) {
      const reserveAccountType = this.getReserveAccountType(transaction);
      const reserveType = this.getCategoryDisplayName(reserveAccountType);
      const category = this.getCategoryDisplayNameFromTransaction(transaction.category);
      
      if (!reserveType || reserveType === '—') {
        return '—';
      }
      
      if (!category) {
        return reserveType;
      }
      
      return `${reserveType} - ${category}`;
    },

    getCurrencyOptions() {
      return Object.values(Currency);
    },

    getCreateExportParams() {
      return {
        resource_type: ResourceType.TRANSACTION,
        transaction_filter: {
          ...this.getCommonQueryParams(),
          cursor: undefined,
          size: undefined,
          filter_type: "RESERVE_TRANSACTION",
          requested_from: "Reserve Transactions"
        }
      };
    },

    tooltipContent() {
      if (this.searchQuery !== '') {
        return this.$t('transactions.searchExport');
      }
      if (this.transactions.length === 0) {
        return this.$t('transactions.noExport');
      }
      return '';
    }
  }
}
</script>

<style scoped lang="scss">
// ReserveTransactionsTable nests TableView inside its own root div plus an extra
// .tab-content-container in the parent view. This breaks the `height: 100%` cascade
// on #infinite-list (defined in TableView.vue), causing .table-container to scroll
// instead of #infinite-list — so the scroll listener never fires.
// Explicitly capping #infinite-list to the same 77vh used on .table-container
// makes it self-constrained and restores correct infinite-scroll behaviour.
::v-deep #infinite-list {
  max-height: 77vh;
}

.reserve-transactions-container {
  & .transaction-column,
  & .reserve-type-container,
  & .amount-container {
    padding-top: 8px;
  }

  & .filters-and-search-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;

    & .filter-container {
      flex: 1;
    }
  }

  & .search-container {
    width: 320px;
    margin-bottom: 16px;
  }

  & .header-container {
    background-color: $color_white;
    width: 100%;
    height: fit-content;
    border: 1px solid $neutral_100;
    border-radius: 6px 6px 0 0;
    display: flex;
    flex-direction: row;
    justify-content: right;
    padding: 15px 25px 5px 25px;

    & .transaction-header-count {
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: $neutral_800;
      width: 100%;
      text-align: left;
      align-self: center;
      padding-bottom: 10px;
      display: flex;

      & .showing-count-top {
        display: inline-block;
        justify-content: left;

        & .pagination {
          font-weight: $fw_bold;
        }

        &.hide-loader {
          display: none;
          visibility: hidden;
        }
      }

      & .loader {
        display: inline-block;
        width: 30px;
        height: 30px;
        border: 3px solid $neutral_100;
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 1s ease-in-out infinite;
        justify-content: center;

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        &.hide-loader {
          display: none;
          visibility: hidden;
        }
      }
    }
  }

  & .transaction-column {
    display: flex;
    flex-direction: row;

    & .status-tag-container {
      display: flex;
      text-align: left;
      min-width: 70px;
      flex-direction: row;
      justify-content: space-between;
    }

    & .status {
      font-size: 12px;
      padding: 2px 10px;
      border-radius: 10px;
      height: fit-content;
      font-style: normal;
      font-weight: 500;
      line-height: 16px;

      &.success-status {
        background-color: $success_100;
        color: $success_600;
      }

      &.failure-status {
        background-color: $danger_50;
        color: $danger_800;
      }

      &.canceled-status {
        background-color: $warn_50;
        color: $warn_800;
      }

      &.progress-status {
        background-color: $primary_100;
        color: $primary_800;
      }
    }

    & .date-and-txid {
      display: flex;
      flex-direction: column;
      justify-content: start;
      align-items: start;
      margin-left: 12px;

      & .date_box {
        font-size: 14px;
        font-weight: $weight_medium;
        line-height: 20px;
        color: $neutral_800;
      }

      & .txid {
        font-weight: $weight_normal;
        color: $neutral_400;
        font-size: 12px;
        line-height: 20px;
      }
    }
  }

  & .amount-container {
    justify-content: right;
    display: flex;
    flex-direction: row;
    padding-right: 20%;
    padding-left: 20px;
    width: 100%;

    & .amount {
      margin-right: 5px;
      font-weight: $fw_bold;
    }

    & .currency_code {
      color: $neutral_400;
      font-size: 14px;
    }

    & .source-txid {
      font-weight: $weight_normal;
      color: $neutral_800;
    }

    & .empty-placeholder {
      color: $neutral_300;
    }
  }

  & .reserve-type-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    & .reserve-type-line {
      line-height: 20px;
      color: $neutral_1000;

      &.multiple-reserve-type-line {
        display: flex;
        align-items: center;
        gap: 4px;

        & .c-tooltip {
          display: inline-flex;
          align-items: center;
          height: 20px;
        }

        & .multiple-reserve-info-icon {
          display: inline-flex;
          align-items: center;
          color: $neutral_400;
          cursor: pointer;
        }
      }
    }

    & .category-line {
      line-height: 20px;
      margin-top: 2px;
    }

    & .entry-type-badge {
      font-size: 11px;
      font-weight: $weight_medium;
      padding: 1px 6px;
      border-radius: 4px;
      margin-top: 3px;
      background-color: $neutral_100;
      color: $neutral_600;
    }
  }

  & .reserve-balance-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    padding-right: 20%;
    padding-left: 20px;
    width: 100%;

    & .reserve-split-item {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    & .reserve-split-line {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-size: 12px;
      line-height: 16px;
      color: $neutral_800;
      text-align: right;
      gap: 4px;

      & .reserve-label {
        font-weight: $weight_normal;
      }

      & .reserve-amount {
        font-weight: $fw_bold;
      }
    }

    & .currency_code {
      font-size: 12px;
      color: $neutral_400;
      line-height: 16px;
    }

    & .empty-placeholder {
      color: $neutral_300;
      text-align: right;
      font-size: 12px;
    }
  }
}
</style>